/**
 * DATA-17 + DATA-18: Admin Segmentation + Cohort API layer.
 * Maps to: GET /admin/metrics/{segments,segments/users,cohort}
 */

import { apiFetch } from "./client";

// ─── Segment Types ───

export type SegmentDistributionRow = {
  readonly segment: string;
  readonly user_count: number;
};

export type SegmentUserRow = {
  readonly user_id: string;
  readonly segment: string;
  readonly scores: {
    readonly messages: number;
    readonly summaries: number;
    readonly deliveries: number;
    readonly shares: number;
    readonly delivery_favorited: number;
  };
};

// ─── Cohort Types ───

export type CohortRetentionRow = {
  readonly cohort_week: string;
  readonly period: number;
  readonly total_users: number;
  readonly retained_users: number;
  readonly retention_rate: number;
};

// ─── API Calls ───

export async function fetchSegmentDistribution(
  date: string,
): Promise<ReadonlyArray<SegmentDistributionRow>> {
  const qs = new URLSearchParams({ date }).toString();
  const res = await apiFetch<{
    data: { items: ReadonlyArray<SegmentDistributionRow> };
  }>(`/admin/metrics/segments?${qs}`);
  return res.data.items;
}

export async function fetchSegmentUsers(params: {
  readonly date: string;
  readonly segment: string;
  readonly limit?: number;
  readonly offset?: number;
}): Promise<{
  readonly items: ReadonlyArray<SegmentUserRow>;
  readonly total: number;
}> {
  const qs = new URLSearchParams({
    date: params.date,
    segment: params.segment,
    ...(params.limit !== undefined && { limit: String(params.limit) }),
    ...(params.offset !== undefined && { offset: String(params.offset) }),
  }).toString();
  const res = await apiFetch<{
    data: {
      items: ReadonlyArray<SegmentUserRow>;
      total: number;
    };
  }>(`/admin/metrics/segments/users?${qs}`);
  return res.data;
}

export async function fetchCohortRetention(params: {
  readonly start_date: string;
  readonly end_date: string;
  readonly platform?: string;
}): Promise<ReadonlyArray<CohortRetentionRow>> {
  const searchParams: Record<string, string> = {
    start_date: params.start_date,
    end_date: params.end_date,
  };
  if (params.platform) {
    searchParams.platform = params.platform;
  }
  const qs = new URLSearchParams(searchParams).toString();
  const res = await apiFetch<{
    data: { items: ReadonlyArray<CohortRetentionRow> };
  }>(`/admin/metrics/cohort?${qs}`);
  return res.data.items;
}
